import type { AssignmentDocument } from "./AssignmentDocument";
import type { WritingSave } from "@/lib/api/assignments";

const PREFIX = "imam:writing-recovery:v1:";
const DATABASE = "imam-writing-recovery-v1";
const STORE = "copies";
export const WRITING_RECOVERY_CLEARED = "imam:writing-recovery-cleared";
export type RecoveryDurability = "durable" | "fallback" | "memory";
export type WritingRecovery = {
  copyId: string;
  updatedAt: number;
  tenant: string;
  owner: string;
  assignment: string;
  draft: string;
  baseRevision: number;
  document: AssignmentDocument;
  pending: WritingSave | null;
};
export function writingRecoveryKey(tenant: string, owner: string, draft: string, copyId: string) {
  return `${PREFIX}${tenant}:${owner}:${draft}:${copyId}`;
}
let epoch = 0;
let clearing = Promise.resolve();
function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE, { keyPath: "key" });
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    request.onblocked = () => reject(new Error("Recovery database unavailable"));
  });
}
async function transact<T>(mode: IDBTransactionMode, operation: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDatabase();
  try {
    return await new Promise<T>((resolve, reject) => {
      const transaction = db.transaction(STORE, mode, mode === "readwrite" ? { durability: "strict" } : undefined);
      const request = operation(transaction.objectStore(STORE));
      transaction.oncomplete = () => resolve(request.result);
      transaction.onabort = () => reject(transaction.error);
      transaction.onerror = () => reject(transaction.error);
    });
  } finally { db.close(); }
}
function valid(value: WritingRecovery | null, key: string, tenant: string, owner: string, assignment: string, draft: string): value is WritingRecovery {
  return Boolean(value && value.tenant === tenant && value.owner === owner && value.assignment === assignment && value.draft === draft
    && value.copyId && key === writingRecoveryKey(tenant, owner, draft, value.copyId) && Number.isFinite(value.updatedAt)
    && Number.isInteger(value.baseRevision) && value.baseRevision >= 0 && value.document?.type === "doc"
    && (!value.pending || (value.pending.expected_revision === value.baseRevision && value.pending.operation_id && value.pending.document?.type === "doc")));
}
export async function readWritingRecovery(tenant: string, owner: string, assignment: string, draft: string): Promise<{ copy: WritingRecovery; durability: RecoveryDurability } | null> {
  await clearing;
  const generation = epoch;
  let recovered: { copy: WritingRecovery; durability: RecoveryDurability } | null = null;
  const consider = (value: WritingRecovery | null, key: string, durability: RecoveryDurability) => {
    if (valid(value, key, tenant, owner, assignment, draft) && (!recovered || value.updatedAt > recovered.copy.updatedAt)) recovered = { copy: value, durability };
  };
  try {
    const values = await transact<(WritingRecovery & { key: string })[]>("readonly", store => store.getAll());
    for (const { key, ...copy } of values) consider(copy, key, "durable");
  } catch { /* A localStorage copy remains a best-effort fallback. */ }
  try {
    const prefix = `${PREFIX}${tenant}:${owner}:${draft}:`;
    for (let index = 0; index < localStorage.length; index++) {
      const key = localStorage.key(index);
      if (!key?.startsWith(prefix)) continue;
      try { consider(JSON.parse(localStorage.getItem(key) ?? "null"), key, "fallback"); } catch { /* Skip only this malformed record. */ }
    }
  } catch { /* Recovery can be unavailable without preventing editing. */ }
  return generation === epoch ? recovered : null;
}
function sameSnapshot(value: WritingRecovery | undefined, expected: WritingRecovery) {
  return value && value.updatedAt === expected.updatedAt && value.baseRevision === expected.baseRevision
    && JSON.stringify(value.document) === JSON.stringify(expected.document) && JSON.stringify(value.pending) === JSON.stringify(expected.pending);
}
const erasureKey = (tenant: string, owner: string, draft: string) => `${PREFIX}erased:${tenant}:${owner}:${draft}`;
export async function eraseWritingRecovery(tenant: string, owner: string, draft: string) {
  const key = erasureKey(tenant, owner, draft);
  try { localStorage.setItem(key, "true"); } catch { /* Primary tombstone remains available. */ }
  await clearing;
  try {
    await transact("readwrite", store => {
      const marked = store.put({ key, erased: true });
      const copies = store.getAllKeys();
      const prefix = `${PREFIX}${tenant}:${owner}:${draft}:`;
      copies.onsuccess = () => { for (const copy of copies.result) if (typeof copy === "string" && copy.startsWith(prefix)) store.delete(copy); };
      return marked;
    });
  } catch { /* Denied primary. The fallback marker blocks stale writers. */ }
  await removeWritingRecovery(tenant, owner, draft);
}
export async function writeWritingRecovery(value: WritingRecovery, supersedes?: WritingRecovery): Promise<RecoveryDurability> {
  const generation = epoch;
  await clearing;
  if (generation !== epoch) return "memory";
  const key = writingRecoveryKey(value.tenant, value.owner, value.draft, value.copyId);
  const tombstone = erasureKey(value.tenant, value.owner, value.draft);
  let erased = false;
  try { if (localStorage.getItem(tombstone)) return "memory"; } catch { /* Check primary. */ }
  try {
    await transact("readwrite", store => {
      if (generation !== epoch) return store.get(key);
      const retained = store.get(tombstone);
      retained.onsuccess = () => {
        if (retained.result) { erased = true; return; }
      store.put({ ...value, key });
      if (supersedes && supersedes.copyId !== value.copyId) {
        const oldKey = writingRecoveryKey(supersedes.tenant, supersedes.owner, supersedes.draft, supersedes.copyId);
        const previous = store.get(oldKey);
        previous.onsuccess = () => {
          const old = previous.result as WritingRecovery | undefined;
          if (sameSnapshot(old, supersedes)) store.delete(oldKey);
        };
      }
      };
      return retained;
    });
    if (generation !== epoch) { await clearing; return "memory"; }
    if (erased) return "memory";
    try {
      localStorage.removeItem(key);
      if (supersedes) {
        const oldKey = writingRecoveryKey(supersedes.tenant, supersedes.owner, supersedes.draft, supersedes.copyId);
        if (oldKey !== key && localStorage.getItem(oldKey) === JSON.stringify(supersedes)) localStorage.removeItem(oldKey);
      }
    } catch { /* Primary commit is already durable. */ }
    return "durable";
  } catch {
    if (generation !== epoch) return "memory";
    try {
      if (localStorage.getItem(tombstone)) return "memory";
      localStorage.setItem(key, JSON.stringify(value));
      if (supersedes && supersedes.copyId !== value.copyId) {
        const oldKey = writingRecoveryKey(supersedes.tenant, supersedes.owner, supersedes.draft, supersedes.copyId);
        if (localStorage.getItem(oldKey) === JSON.stringify(supersedes)) localStorage.removeItem(oldKey);
      }
      return "fallback";
    }
    catch { return "memory"; }
  }
}
export async function removeWritingRecovery(tenant: string, owner: string, draft: string, copyId?: string, expected?: WritingRecovery) {
  const prefix = `${PREFIX}${tenant}:${owner}:${draft}:`;
  const matches = (key: string) => copyId ? key === writingRecoveryKey(tenant, owner, draft, copyId) : key.startsWith(prefix);
  try {
    for (let index = localStorage.length - 1; index >= 0; index--) {
      const key = localStorage.key(index);
      if (key && matches(key) && (!expected || localStorage.getItem(key) === JSON.stringify(expected))) localStorage.removeItem(key);
    }
  } catch { /* A denied fallback is unreadable. */ }
  await clearing;
  try {
    await transact("readwrite", store => {
      const request = store.getAll();
      request.onsuccess = () => { for (const row of request.result as (WritingRecovery & { key: string })[]) if (matches(row.key) && (!expected || sameSnapshot(row, expected))) store.delete(row.key); };
      return request;
    });
  } catch { /* A denied primary store is unreadable. */ }
}
export function clearWritingRecovery(): Promise<void> {
  epoch++;
  window.dispatchEvent(new Event(WRITING_RECOVERY_CLEARED));
  try {
    for (let index = localStorage.length - 1; index >= 0; index--) {
      const key = localStorage.key(index);
      if (key?.startsWith(PREFIX)) localStorage.removeItem(key);
    }
  } catch { /* A denied fallback is unreadable. */ }
  clearing = clearing.then(async () => { try { await transact("readwrite", store => store.clear()); } catch { /* Denied store. */ } });
  return clearing;
}
